import * as React from 'react';

import {
  ContentSection,
  LocalizedDate,
  Select,
  TextField,
} from '@rematter/ui-web';
import { cn } from '@rematter/ui-web/lib';
import { useAtomValue, useSetAtom } from 'jotai';
import { selectAtom } from 'jotai/utils';
import isEqual from 'lodash/isEqual';

import {
  $itemAliasForm,
  ItemAliasFormState,
} from '../../../_state/itemAliasForm.state';
import { useTranslations } from '../../../i18n/useTranslations';
import {
  enumItemAliasType,
  ItemAliasRecord,
  ItemAliasType,
} from '../../../types/inventoryItem';

import { SelectAccount } from '~/shared/components/select-fields/SelectAccount';
import { SelectMaterial } from '~/shared/components/select-fields/SelectMaterial';

type ItemAliasFormFieldsProps = {
  disabled?: boolean;
  showTimestamps?: boolean;
  showSelectors?: boolean;
  itemAlias?: ItemAliasRecord | null;
  className?: string;
};

const formSelector = (state: ItemAliasFormState) => ({
  itemId: state.itemId,
  itemName: state.itemName,
  accountId: state.accountId,
  accountName: state.accountName,
  aliasName: state.aliasName,
  aliasCode: state.aliasCode,
  aliasType: state.aliasType,
  notes: state.notes,
});

export const ItemAliasFormFields = ({
  disabled = false,
  showTimestamps = false,
  showSelectors = false,
  itemAlias,
  className,
}: ItemAliasFormFieldsProps) => {
  const t = useTranslations();

  const selectedFormAtom = React.useMemo(
    () => selectAtom($itemAliasForm, formSelector, isEqual),
    [],
  );

  const form = useAtomValue(selectedFormAtom);

  const updateForm = useSetAtom($itemAliasForm);

  const aliasTypeOptions = React.useMemo(
    () => [
      {
        key: enumItemAliasType.Buy,
        label: t.inventoryView.itemAliasCreateDialog.aliasTypes.buy,
      },
      {
        key: enumItemAliasType.Sell,
        label: t.inventoryView.itemAliasCreateDialog.aliasTypes.sell,
      },
    ],
    [t],
  );

  const handleFieldChange = React.useCallback(
    <K extends keyof ItemAliasFormState>(field: K) =>
      (value: ItemAliasFormState[K]) => {
        updateForm((prev) => ({
          ...prev,
          [field]: value,
        }));
      },
    [updateForm],
  );

  return (
    <div className={cn('@ItemAliasFormFields', className)}>
      <ContentSection
        title={t.inventoryView.itemAliasDetailView.sections.aliasAssociation}
        variant="panel"
      >
        <div className="grid gap-4 md:grid-cols-2">
          {showSelectors ? (
            <>
              <SelectMaterial
                label={t.inventoryView.itemAliasCreateDialog.fields.item}
                value={form.itemId || undefined}
                onChange={(value) => handleFieldChange('itemId')(value ?? '')}
                placeholder={
                  t.inventoryView.itemAliasCreateDialog.placeholders.item
                }
                isRequired
                isDisabled={disabled}
              />
              <SelectAccount
                label={t.inventoryView.itemAliasCreateDialog.fields.account}
                value={form.accountId || undefined}
                onChange={(value) =>
                  handleFieldChange('accountId')(value ?? '')
                }
                placeholder={
                  t.inventoryView.itemAliasCreateDialog.placeholders.account
                }
                isRequired
                isDisabled={disabled}
              />
            </>
          ) : (
            <>
              <div className="flex flex-col gap-1">
                <span className="text-sm font-medium text-neutral/60">
                  {t.inventoryView.itemAliasCreateDialog.fields.item}
                </span>
                <span className="text-sm">{form.itemName || '-'}</span>
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-sm font-medium text-neutral/60">
                  {t.inventoryView.itemAliasCreateDialog.fields.account}
                </span>
                <span className="text-sm">{form.accountName || '-'}</span>
              </div>
            </>
          )}
        </div>
      </ContentSection>

      <ContentSection
        title={t.inventoryView.itemAliasDetailView.sections.aliasInformation}
        variant="panel"
      >
        <div className="grid gap-4 md:grid-cols-2">
          <TextField
            label={t.inventoryView.itemAliasCreateDialog.fields.aliasName}
            value={form.aliasName}
            onChange={(value) => handleFieldChange('aliasName')(value)}
            placeholder={
              t.inventoryView.itemAliasCreateDialog.placeholders.aliasName
            }
            isRequired
            isDisabled={disabled}
          />

          <TextField
            label={t.inventoryView.itemAliasCreateDialog.fields.aliasCode}
            value={form.aliasCode}
            onChange={(value) => handleFieldChange('aliasCode')(value)}
            placeholder={
              t.inventoryView.itemAliasCreateDialog.placeholders.aliasCode
            }
            isDisabled={disabled}
          />

          <Select
            label={t.inventoryView.itemAliasCreateDialog.fields.type}
            value={form.aliasType}
            onChange={(value) =>
              handleFieldChange('aliasType')(
                (value as ItemAliasType) ?? enumItemAliasType.Sell,
              )
            }
            options={aliasTypeOptions}
            isDisabled={disabled}
          />
        </div>
      </ContentSection>

      {showTimestamps && (
        <ContentSection
          title={t.inventoryView.itemAliasDetailView.sections.timestamps}
          variant="panel"
        >
          <div className="grid gap-4 md:grid-cols-2">
            <div className="flex flex-col gap-1">
              <span className="text-sm text-neutral/60">
                {t.inventoryView.itemAliasDetailView.fields.created}
              </span>
              <span className="text-sm font-medium">
                {itemAlias?.timestamps.createdAt ? (
                  <LocalizedDate date={itemAlias.timestamps.createdAt} />
                ) : (
                  '-'
                )}
              </span>
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-sm text-neutral/60">
                {t.inventoryView.itemAliasDetailView.fields.updated}
              </span>
              <span className="text-sm font-medium">
                {itemAlias?.timestamps.updatedAt ? (
                  <LocalizedDate date={itemAlias.timestamps.updatedAt} />
                ) : (
                  '-'
                )}
              </span>
            </div>
          </div>
        </ContentSection>
      )}

      <ContentSection
        title={t.inventoryView.itemAliasDetailView.sections.additional}
        variant="panel"
        hideDivider
      >
        <TextField
          label={t.inventoryView.itemAliasCreateDialog.fields.notes}
          value={form.notes}
          onChange={(value) => handleFieldChange('notes')(value)}
          placeholder={t.inventoryView.itemAliasCreateDialog.placeholders.notes}
          textArea
          isDisabled={disabled}
        />
      </ContentSection>
    </div>
  );
};
